import axios from 'axios';
import {
  ApiSuccessResponse,
  AuthResponse,
  MovieResponse,
  MoviesResponse,
  PaymentStatusResponse,
  PaypalCaptureResponse,
  PaypalCheckoutResponse,
  PlaybackResponse,
  Rental,
  StripeCheckoutResponse,
  User,
} from '../types/api';
import { getCurrentLocale } from '../i18n/I18nContext';

const configuredApiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
const isLocalFrontend =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const API_BASE_URL = isLocalFrontend && configuredApiBaseUrl.includes('cinemazone-api-uat')
  ? 'http://localhost:3000'
  : configuredApiBaseUrl;
const TOKEN_KEY = 'viu_movies_token';
const USER_KEY = 'viu_movies_user';

type ApiErrorPayload = {
  message?: string | string[];
};

export const getApiErrorMessage = (error: unknown, fallback: string) => {
  if (axios.isAxiosError<ApiErrorPayload>(error)) {
    const message = error.response?.data?.message;
    if (Array.isArray(message) && message.length > 0) {
      return message.join(', ');
    }
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }

  return fallback;
};

export const getToken = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return token && token !== 'undefined' && token !== 'null' ? token : null;
};
export const setToken = (token: string) => {
  if (!token) {
    clearToken();
    throw new Error('Authentication token was missing from the API response');
  }
  localStorage.setItem(TOKEN_KEY, token);
};
export const getStoredUser = (): User | null => {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw || raw === 'undefined' || raw === 'null') return null;
  try {
    return JSON.parse(raw) as User;
  } catch {
    localStorage.removeItem(USER_KEY);
    return null;
  }
};
export const setStoredUser = (user: User) => localStorage.setItem(USER_KEY, JSON.stringify(user));
export const clearToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

const api = axios.create({ baseURL: API_BASE_URL, withCredentials: true });

api.interceptors.request.use((config) => {
  const token = getToken();
  config.headers['Accept-Language'] = getCurrentLocale();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const unwrapAuthResponse = (payload: AuthResponse | ApiSuccessResponse<AuthResponse>): AuthResponse => {
  return 'data' in payload ? payload.data : payload;
};

const persistAuth = (auth: AuthResponse) => {
  setToken(auth.token);
  setStoredUser(auth.user);
  return auth;
};

export const buildSocialLoginUrl = (provider: 'google', returnTo = '/') => {
  return `${API_BASE_URL}/auth/${provider}?returnTo=${encodeURIComponent(returnTo)}`;
};

export const signup = async (name: string, email: string, password: string): Promise<AuthResponse> => {
  const response = await api.post<AuthResponse | ApiSuccessResponse<AuthResponse>>('/auth/signup', { name, email, password });
  const auth = unwrapAuthResponse(response.data);
  return persistAuth(auth);
};

export const login = async (email: string, password: string): Promise<AuthResponse> => {
  const response = await api.post<AuthResponse | ApiSuccessResponse<AuthResponse>>('/auth/login', { email, password });
  const auth = unwrapAuthResponse(response.data);
  return persistAuth(auth);
};

export const getMe = async (): Promise<User> => {
  const response = await api.get<ApiSuccessResponse<{ user: User }> | { user: User }>('/auth/me');
  return 'data' in response.data ? response.data.data.user : response.data.user;
};

export const getMovies = async (params: Record<string, string>): Promise<MoviesResponse> => {
  const response = await api.get<MoviesResponse>('/movies', { params });
  return response.data;
};

export const getFeaturedMovies = async (): Promise<{ data: MovieResponse['data'][] }> => {
  const response = await api.get('/movies/featured');
  return response.data;
};

export const getMovie = async (id: string): Promise<MovieResponse> => {
  const response = await api.get<MovieResponse>(`/movies/${id}`);
  return response.data;
};

export const stripeCheckout = async (movieId: number): Promise<StripeCheckoutResponse> => {
  const response = await api.post<ApiSuccessResponse<StripeCheckoutResponse>>('/stripe/create-checkout-session', { movieId });
  return response.data.data;
};

export const stripeGuestCheckout = async (movieId: number, email: string): Promise<StripeCheckoutResponse> => {
  const response = await api.post<ApiSuccessResponse<StripeCheckoutResponse>>('/stripe/create-guest-checkout-session', { movieId, email });
  return response.data.data;
};

export const paypalCheckout = async (movieId: number): Promise<PaypalCheckoutResponse> => {
  const response = await api.post<ApiSuccessResponse<PaypalCheckoutResponse>>('/paypal/create-order', { movieId });
  return response.data.data;
};

export const paypalGuestCheckout = async (movieId: number, email: string): Promise<PaypalCheckoutResponse> => {
  const response = await api.post<ApiSuccessResponse<PaypalCheckoutResponse>>('/paypal/create-guest-order', { movieId, email });
  return response.data.data;
};

export const paypalCaptureOrder = async (orderId: string, accessToken?: string): Promise<PaypalCaptureResponse> => {
  const response = await api.post<ApiSuccessResponse<PaypalCaptureResponse>>('/paypal/capture-order', { orderId, accessToken, locale: getCurrentLocale() });
  return response.data.data;
};

export const getPaymentStatus = async (paymentId: number): Promise<PaymentStatusResponse> => {
  const response = await api.get<ApiSuccessResponse<PaymentStatusResponse>>(`/payments/${paymentId}`);
  return response.data.data;
};

export const getGuestPaymentStatus = async (paymentId: number, accessToken: string): Promise<PaymentStatusResponse> => {
  const response = await api.get<ApiSuccessResponse<PaymentStatusResponse>>(`/payments/guest/${paymentId}/${encodeURIComponent(accessToken)}`);
  return response.data.data;
};

export const getGuestPaymentStatusByAccessToken = async (accessToken: string): Promise<PaymentStatusResponse> => {
  const response = await api.get<ApiSuccessResponse<PaymentStatusResponse>>(`/payments/guest/${encodeURIComponent(accessToken)}`);
  return response.data.data;
};

export const getRentals = async (): Promise<{ data: Rental[] }> => {
  const response = await api.get('/payments/rentals');
  return response.data;
};

export const getPlayback = async (movieId: string): Promise<PlaybackResponse> => {
  const response = await api.get<ApiSuccessResponse<PlaybackResponse> | PlaybackResponse>(`/movies/${movieId}/play`);
  return 'data' in response.data ? response.data.data : response.data;
};

export const getGuestPlayback = async (accessToken: string): Promise<PlaybackResponse> => {
  const response = await api.get<ApiSuccessResponse<PlaybackResponse> | PlaybackResponse>(`/movies/guest/${encodeURIComponent(accessToken)}/play`, { withCredentials: true });
  return 'data' in response.data ? response.data.data : response.data;
};